import type { ViewQueryRecordNode } from '@salescore/core'
import { Result } from 'effect'

import type { DeepReadonly } from '../types/DeepReadonly'

/**
 * 子レコードを非破壊的に置き換える関数
 * rootRecordNode を nodePath を元に辿って、 newRecordNode と id が一致するノードを置き換える
 *
 * @param rootRecordNode ルートレコードノード
 * @param nodePath テーブルノード ID の配列
 * @param newRecordNode 新しいレコードノード（ ID は置換対象のノードの ID と一致している必要がある）
 * @returns Result
 *  Success: 置き換え後のルートレコードノード
 *  Failure: エラーメッセージ
 */
export const replaceChildRecordNode = (
  rootRecordNode: DeepReadonly<ViewQueryRecordNode>,
  nodePath: DeepReadonly<string[]>,
  newRecordNode: DeepReadonly<ViewQueryRecordNode>,
): Result.Result<DeepReadonly<ViewQueryRecordNode>, undefined> => {
  // 子供のパスでない場合はエラー
  if (nodePath.length < 2) {
    return Result.fail(undefined)
  }

  // 最初に置き換えが可能かチェック（読み取り専用操作）
  const canReplace = checkPath(rootRecordNode, nodePath.slice(1), newRecordNode.id)
  if (!canReplace) {
    return Result.fail(undefined)
  }

  // 非破壊的に置き換えを実行
  const newRootNode = replaceNodeRecursively(rootRecordNode, nodePath.slice(1), newRecordNode)

  // 変更後のルートノードを返す、ノードパスはチェック済みなので必ず成功する
  return Result.succeed(newRootNode)
}

const checkPath = (
  node: DeepReadonly<ViewQueryRecordNode>,
  remainingPath: string[],
  newRecordNodeId: string | undefined,
): boolean => {
  if (remainingPath.length === 0) return false

  const [nextNodeName, ...restPath] = remainingPath
  const childTable = node.children.find((table) => table.nodeName === nextNodeName)
  if (childTable === undefined) return false

  if (restPath.length === 0) {
    return childTable.children.some((record) => record.id === newRecordNodeId)
  }

  return childTable.children.some((childRecord) => checkPath(childRecord, restPath, newRecordNodeId))
}

/**
 * 再帰的にnodePathを辿って置き換える関数
 * @param currentNode
 * @param remainingPath
 * @param newRecordNode
 * @returns 置き換え後の新しいノード
 */
const replaceNodeRecursively = (
  currentNode: DeepReadonly<ViewQueryRecordNode>,
  remainingPath: string[],
  newRecordNode: DeepReadonly<ViewQueryRecordNode>,
): DeepReadonly<ViewQueryRecordNode> => {
  // パスが空の場合は元のノードをそのまま返す
  if (remainingPath.length === 0) {
    return currentNode
  }

  // 次のノード名を取得
  const [nextNodeName, ...restPath] = remainingPath

  // 子テーブルをmapで処理し、条件に合うもののみ更新
  return {
    ...currentNode,
    children: currentNode.children.map((childTable) =>
      // 対象のテーブルでない場合はそのまま返す
      childTable.nodeName === nextNodeName
        ? {
            ...childTable,
            children: childTable.children.map((child) =>
              restPath.length === 0
                ? // パスが終端に到達した場合（残りパスが1つだけ）、該当するレコードのみ置き換え
                  // eslint-disable-next-line sonarjs/no-nested-conditional
                  child.id === newRecordNode.id
                  ? newRecordNode
                  : child
                : // まだパスの途中の場合、再帰的に探索して置き換え
                  replaceNodeRecursively(child, restPath, newRecordNode),
            ),
          }
        : childTable,
    ),
  }
}
